/*
 * Pure, side-effect-free helpers for mapping between UE site resource paths
 * and AEM DAM folder paths. No imports, no network.
 *
 * Convention (per product requirement):
 *   site root  /content/{slug}          -> DAM folder  /content/dam/{slug}
 *   e.g.       /content/wknd-universal  -> /content/dam/wknd-universal
 */

/**
 * Strip the UE URN prefix from a resource path. UE gives resources in the
 * shape `urn:<connectionName>:/content/...` — we want just `/content/...`.
 * Idempotent: passing an already-clean path returns it unchanged.
 */
export function stripResourceUrn(resource) {
  if (typeof resource !== 'string' || !resource) return '';
  return resource.replace(/^urn:[^:]+:/, '');
}

/**
 * Extract the site slug from any resource path that begins with /content/{slug}.
 * Accepts both clean paths and UE `urn:<conn>:/content/...` URNs.
 * Ignores /content/dam (which is an assets path, not a site path).
 * Returns '' when the input doesn't look like a site path.
 */
export function siteSlugFromResource(resource) {
  const clean = stripResourceUrn(resource);
  if (!clean) return '';
  const m = clean.match(/^\/content\/([^/]+)/);
  if (!m) return '';
  const slug = m[1];
  if (slug === 'dam') return '';
  return slug;
}

/**
 * Extract the site slug from an editor location URL (e.g.
 * `https://author-...adobeaemcloud.com/content/wknd-universal/en/home.html?...`).
 * Useful as a fallback when editables[] isn't populated or doesn't carry
 * resource paths.
 */
export function siteSlugFromLocation(locationUrl) {
  if (typeof locationUrl !== 'string' || !locationUrl) return '';
  let pathname = locationUrl;
  try {
    pathname = new URL(locationUrl).pathname;
  } catch (_) {
    // Not a valid URL — maybe already a pathname. Keep as-is.
  }
  return siteSlugFromResource(pathname);
}

/**
 * Extract the full site root (/content/{slug}) from a resource path.
 * Returns '' if the input isn't under /content or is under /content/dam.
 */
export function sitePathFromResource(resource) {
  const slug = siteSlugFromResource(resource);
  return slug ? `/content/${slug}` : '';
}

/**
 * Map a site slug to its DAM folder. Returns '' for empty input.
 */
export function damFolderFromSiteSlug(slug) {
  if (typeof slug !== 'string' || !slug) return '';
  return `/content/dam/${slug}`;
}

/**
 * Given a list of UE editables (from editorState.editables[]), try to find
 * the most plausible site slug by scanning their `resource` fields.
 * Returns the first non-empty slug encountered.
 */
export function siteSlugFromEditables(editables) {
  if (!Array.isArray(editables)) return '';
  for (const e of editables) {
    const s = siteSlugFromResource(e && e.resource);
    if (s) return s;
  }
  return '';
}

/**
 * Heuristic: does this resource path or type hint at an image editable?
 * Used to decide whether "Replace" is meaningful for the current selection.
 */
export function isImageLikeResource(editable) {
  if (!editable) return false;
  const probe = `${editable.type || ''} ${editable.name || ''} ${editable.label || ''} ${editable.resource || ''}`.toLowerCase();
  return probe.includes('image');
}

/**
 * Extract the JCR page path from an editor location URL.
 *
 * e.g. "https://author-p165802-e1765367.adobeaemcloud.com/content/khermand/6a.../us/en/index.html?ext=..."
 *   → "/content/khermand/6a.../us/en/index"
 *
 * Strips query params, hash, and the .html extension.
 * Returns '' if the URL doesn't contain a /content/ path.
 */
export function pagePathFromLocation(locationUrl) {
  if (typeof locationUrl !== 'string' || !locationUrl) return '';
  let pathname = locationUrl;
  try {
    pathname = new URL(locationUrl).pathname;
  } catch (_) { /* already a pathname */ }
  pathname = pathname.replace(/\.html$/, '');
  if (!pathname.startsWith('/content/')) return '';
  return pathname;
}

/**
 * Derive the "pages root" — the JCR path whose children are the site's pages —
 * from ANY page under it (not just the homepage). This lets "Discover Site
 * Pages" work while editing a deep page like `.../en/campaigns`.
 *
 * Works for both site structures:
 *   /content/{hash}/language-masters/en/campaigns → /content/{hash}/language-masters/en
 *   /content/{hash}/language-masters/en/index     → /content/{hash}/language-masters/en
 *   /content/{hash}/language-masters/en           → /content/{hash}/language-masters/en
 *   /content/{user}/{hash}/us/en/products/chair   → /content/{user}/{hash}/us/en
 *   /content/{user}/{hash}/us/en/index            → /content/{user}/{hash}/us/en
 *   /content/{user}/{hash}/us/en                   → /content/{user}/{hash}/us/en
 */
export function pagesRootFromPagePath(pagePath) {
  if (typeof pagePath !== 'string' || !pagePath) return '';
  const segs = pagePath.split('/').filter(Boolean); // ['content', hash, 'language-masters', 'en', ...]

  // Structure A: cut right after the locale that follows "language-masters".
  const lmIdx = segs.indexOf('language-masters');
  if (lmIdx !== -1 && segs.length > lmIdx + 1) {
    return '/' + segs.slice(0, lmIdx + 2).join('/');
  }

  // Structure B: /content/{user}/{hash}/{country}/{lang}/... → the language
  // root is the first 5 segments.
  if (segs[0] === 'content' && segs.length >= 5) {
    return '/' + segs.slice(0, 5).join('/');
  }

  // Unknown/shallow structure: just drop a trailing "/index".
  return pagePath.replace(/\/index$/, '');
}

/**
 * Extract the pages root directly from an editable resource URN.
 * The resource looks like:
 *   urn:aemconnection:/content/khermand/{hash}/us/en/jcr:content/root/section/...
 * We strip the URN prefix, then everything from jcr:content onwards to get the
 * page path, then apply pagesRootFromPagePath.
 */
export function pagesRootFromResource(resource) {
  const clean = stripResourceUrn(resource);
  if (!clean) return '';
  const jcrIdx = clean.indexOf('/jcr:content');
  if (jcrIdx === -1) return '';
  const pagePath = clean.substring(0, jcrIdx);
  return pagesRootFromPagePath(pagePath);
}

/**
 * Best-effort extraction of pagesRoot from editables list.
 * Scans resources for a /jcr:content path and derives the pages root.
 */
export function pagesRootFromEditables(editables) {
  if (!Array.isArray(editables)) return '';
  for (const e of editables) {
    const r = pagesRootFromResource(e && e.resource);
    if (r) return r;
  }
  return '';
}
